import { LogsApiSystemColumns } from '../../TypeScript/constants';
import { JsonDataframe } from '../../TypeScript/types';
import { putLogDataItems } from '../authenticated-requests/logs';
import { errorHandler } from '../error/error-handler';

export const LOG_REQUEST_SIZE_BASE_SIZE = 10000;
export const LOG_REQUEST_SIZE_REDUCTION_FACTOR = 100;
export const LOG_REQUEST_SIZE_MIN = 1000;

/**
 * Calculates the request size (number of "rows") for a
 * PUT operation based on the number of columns.
 * The size decreases linearly with the number of columns,
 * with a minimum limit of 1,000 and a maxium of 9,990.
 * The purpose is to avoid a 413 "Request Entity Too Large" error.
 *
 * @param columnCount - The number of columns in the dataset.
 * @returns The calculated request size.
 *
 * ### Examples:
 * - For 1 column: `10000 - 1 * 100 = 9900`
 * - For 5 columns: `10000 - 5 * 100 = 9500`
 * - For 90 columns: `10000 - 90 * 100 = 1000` (minimum limit)
 * - For 100 columns: `10000 - 100 * 100 = 0` (defaults to 1000)
 */
export const getRequestSize = (columnCount: number) => {
  const baseSize = LOG_REQUEST_SIZE_BASE_SIZE;
  const reductionFactor = LOG_REQUEST_SIZE_REDUCTION_FACTOR;
  return Math.max(
    baseSize - columnCount * reductionFactor,
    LOG_REQUEST_SIZE_MIN,
  );
};

/**
 * Create an empty request body for PUT /logs/{logId}/data to populate with up to 10000 log rows
 * @returns empty request body
 * @example
 * ```
  const columns = ['depth_from','depth_to','drillhole_id','cluster_name'];
  const emptyRequestObject = getEmptyRequestObject(columns);
  // emptyRequestObject:
  // {
  //   depth_from: [],
  //   depth_to: [],
  //   drillhole_id: [],
  //   cluster_name: []
  // }
  * ```
  */
const getEmptyRequestObject = (columns: string[]): Record<string, any> => {
  const emptyRequestObject: Record<string, any> = {};
  columns.forEach((column: string) => {
    emptyRequestObject[column] = [];
  });
  return emptyRequestObject;
};

/**
 * Turn dataframe into an array of logs api request bodies
 *
 * Returns list of request bodies because dataset might be too large for a single PUT request
 *
 * @param dataframe
 * @returns list of request bodies for POST /log/{logId}/data
 */
export const getLogsFromDataframe = (
  dataframe: JsonDataframe,
): Record<string, any>[] => {
  const data: Record<string, any>[] = [];
  const columns: string[] = Object.keys(dataframe);
  const requestSize = getRequestSize(columns.length);
  const rowIndices: string[] = Object.keys(dataframe[columns[0]] || {});
  rowIndices.forEach((rowIndex: string) => {
    if (
      !data[data.length - 1] ||
      data[data.length - 1][LogsApiSystemColumns.drillholeId]?.length ===
        requestSize
    ) {
      // if no request object yet, or latest request object has reached max size => start a new request object
      const emptyRequestObject = getEmptyRequestObject(columns);
      data.push(emptyRequestObject);
    }
    columns.forEach((column: string) => {
      const value: any = dataframe[column]?.[rowIndex];
      // push value to latest request object
      data[data.length - 1][column].push(value);
    });
  });
  return data;
};

/**
 * Call PUT /logs/{logId}/data for each request body
 * @param requestBodies (divided into batches of 10000)
 * @param logId log ID string for endpoint path
 */
const executeMultiplePutLogDataItemsRequests = (
  requestBodies: Record<string, any>[],
  logId: string,
  projectId: string,
  company: string,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const requests: Promise<void>[] = requestBodies.map(
      (requestBody: Record<string, any>): Promise<void> => {
        return putLogDataItems(company, projectId, logId, requestBody, true);
      },
    );
    Promise.all(requests)
      .then(() => {
        resolve();
      })
      .catch((err) => {
        errorHandler(err);
        if (err.length > 0) {
          reject(err[0]);
        } else {
          reject(err);
        }
      });
  });
};

/**
 * Puts data in logs
 *
 * Handles parsing the dataset into 1+ request bodies
 */
export const putDataframeInLog = async (
  dataframe: JsonDataframe,
  logId: string,
  project: string,
  company: string,
): Promise<void> => {
  const requests = getLogsFromDataframe(dataframe);
  return await executeMultiplePutLogDataItemsRequests(
    requests,
    logId,
    project,
    company,
  );
};
